import dataStorageManager, { BucketDefaultIDs, DMS_Operation } from '../../managers/data/DataStorageManager'
import dataQueryManager from '../../managers/network/DataQueryManager'
import { IArmador } from './ArmadoresServices'
import { IProveedor } from './ProveedoresServices'
import { IRequest } from './RequestsServices'
import { ITripulante } from './TripulantesServices'
import { defaultIUser, IUser } from './UsersServices'

export enum SystemModules {
  SISTEMA = 0,
  ARMADORES = 1,
  // Para armadores mas especificos se usa 1;xxxx donde xxxx es el id del armador
  EMBARCACIONES = 2,
  // Para embarcaciones mas especificas se usa 2;xxxx donde xxxx es el id de la embarcacion
  TRIPULANTES = 3,
  // Para tripulantes mas especificos se usa 3;xxxx donde xxxx es el id del tripulante
  PROVEEDORES = 4,
  // Para proveedores mas especificos se usa 4;xxxx donde xxxx es el id del proveedor
  REPRESENTANTES = 14,
  // Para representantes mas especificos se usa 14;xxxx donde xxxx es el id del representante
  USUARIOS = 5,
  TARIFAS = 6,
  VIAJES = 7,
  SERVICIOS = 8,
  COBROS = 9,
  REPORTES = 10,
  PREACREDITACIONES = 11,
  AUDITORIA = 12,
  INFORMACION_PERSONAL = 13,
}

export type SystemModulesData = SystemModules | number // Para permitir el uso de modulos especificos con el formato modulo;id

// Sistema VAME - Permisos basicos: 0x0001, 0x0010, 0x0100, 0x1000 (Eliminar, Modificar, Agregar, Ver)
export enum ActionsPermissions {
  NONE = 0, // Denegación explícita del módulo/embarcación
  VER = 0x1000,
  AGREGAR = 0x0100,
  MODIFICAR = 0x0010,
  ELIMINAR = 0x0001,
  FULL = 0x1111, // Todos los permisos concedidos
}

export interface representanteData {
  tipo: 'armador' | 'proveedor'
  id_empresa: number
  id_solicitud: number
  permisos: number[]
}

export interface OperatedRepresentanteData extends representanteData {
  empresaData: IArmador | IProveedor | undefined
  solicitudData: IRequest | undefined
  permisosData: IPermisoControl[]
}

export interface IRepresentante extends IUser {
  representados: representanteData[]
}

export interface OperatedRepresentante extends IRepresentante {
  representadosData: OperatedRepresentanteData[]
  usuarioArmadorData: IArmador | undefined
  usuarioTripulanteData: ITripulante | undefined
  usuarioPersonalRequests: IRequest[]
}

export const defaultIRepresentante: IRepresentante = {
  ...defaultIUser,
  representados: [],
}

export interface IPermisoControl {
  id: number
  representante: number // Id de solicitud de representante a la que se le asignan los permisos
  prioridad: number
  // Id de la embarcación, como texto ("{id}"). permiso_control controla únicamente embarcaciones.
  // Formato viejo "2;{id}" puede coexistir hasta que corra la migración; ver embarcacionIdDeModulo.
  modulo: string
  permiso: ActionsPermissions
}

export interface OperatedPermisoControl extends IPermisoControl {
  representanteData: OperatedRepresentante
  modulos: SystemModulesData[]
}

export default class RepresentantesServices {
  private bucketID: string | null = null

  constructor() {
    this.bucketID = dataStorageManager.registerBucket<IPermisoControl>(this.getAllPermisos.bind(this), {
      refreshRatio: 5 * 60 * 1000,
      bucketID: BucketDefaultIDs.PERMISOS,
      operations: new Map<string, DMS_Operation>([
        [
          'insertPermiso',
          {
            operation: this.insertPermiso.bind(this),
            reloadAfter: true,
          },
        ],
        [
          'updatePermiso',
          {
            operation: this.updatePermiso.bind(this),
            reloadAfter: true,
          },
        ],
      ]),
    })
  }

  startup() {}

  async getAllPermisos(): Promise<IPermisoControl[]> {
    try {
      const response = await dataQueryManager.addQuery<IPermisoControl | IPermisoControl[]>({
        query: `${import.meta.env.VITE_BASE_URL}/representante_interino/1/op_select_permiso_control`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
      })
      if (response.data && Array.isArray(response.data)) return response.data
      if (response.data) return [response.data]
    } catch (error) {
      console.error('Error fetching permisos:', error)
    }
    return []
  }

  async insertPermiso(permisoData: IPermisoControl): Promise<boolean> {
    try {
      const body = {
        op_insert_permiso_control: {
          representante: permisoData.representante,
          prioridad: permisoData.prioridad,
          modulo: permisoData.modulo,
          permiso: permisoData.permiso,
        },
      }
      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/representante_interino/1/op_insert_permiso_control`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') return true
    } catch (error) {
      console.error('Error inserting permiso:', error)
    }
    return false
  }

  async updatePermiso(permisoData: IPermisoControl): Promise<boolean> {
    try {
      const body = {
        op_update_permiso_control: {
          id: permisoData.id,
          representante: permisoData.representante,
          prioridad: permisoData.prioridad,
          modulo: permisoData.modulo,
          permiso: permisoData.permiso,
        },
      }
      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/representante_interino/1/op_update_permiso_control`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') return true
    } catch (error) {
      console.error('Error updating permiso:', error)
    }
    return false
  }
}
